import type { KrPaymentOrder } from '@entities/kr-payment/model'
import { podoFetcher } from '@server/infrastructure/external-apis/podo/client'
import { BaseResponseSchema, RawCouponResponse } from '@server/schemas'
import { queryOptions } from '@tanstack/react-query'

type PaymentCouponResponse = Omit<RawCouponResponse, 'data'> & {
  data: Array<RawCouponResponse['data'][number] & { non_applicable_reason?: string | null }>
}

type CouponPaymentContext = {
  subscribeId: string
  productId?: string | null
  purchaseOrderId?: string | null
  paymentMethod?: KrPaymentOrder['paymentMethod'] | null
}
const orderCouponQuery = (paymentMethod?: KrPaymentOrder['paymentMethod'] | null) =>
  paymentMethod ? `?${new URLSearchParams({ paymentMethod })}` : ''

export const paymentEntityQueries = {
  all: ['payment'],

  // queryKeys
  getCouponForPaymentQueryKey: (requestDto: RequestQueryOptions<CouponPaymentContext>) => [
    ...paymentEntityQueries.all,
    'getCouponForPayment',
    requestDto,
  ],
  getDiscountedPriceQueryKey: (requestDto: RequestQueryOptions<CouponPaymentContext & { couponId: string }>) => [
    ...paymentEntityQueries.all,
    'getDiscountedPrice',
    requestDto,
  ],
  getProductCouponForPaymentQueryKey: (requestDto: RequestQueryOptions<{ productId: string }>) => [
    ...paymentEntityQueries.all,
    'getProductCouponForPayment',
    requestDto,
  ],
  getProductDiscountedPriceQueryKey: (requestDto: RequestQueryOptions<{ productId: string; couponId: string }>) => [
    ...paymentEntityQueries.all,
    'getProductDiscountedPrice',
    requestDto,
  ],

  getCouponForPayment: (requestDto: RequestQueryOptions<CouponPaymentContext>, lockedScope?: string | null) =>
    queryOptions({
      queryKey: lockedScope
        ? [...paymentEntityQueries.all, 'lockedCoupons', requestDto, lockedScope]
        : requestDto.purchaseOrderId
          ? [
              ...paymentEntityQueries.all,
              'preparedOrderCoupons',
              requestDto.purchaseOrderId,
              requestDto.paymentMethod,
              requestDto.bearerToken,
            ]
          : requestDto.productId
            ? paymentEntityQueries.getProductCouponForPaymentQueryKey({
                productId: requestDto.productId,
                bearerToken: requestDto.bearerToken,
              })
            : paymentEntityQueries.getCouponForPaymentQueryKey(requestDto),
      queryFn: async () => {
        if (lockedScope) {
          return []
        }
        if (requestDto.purchaseOrderId) {
          return await podoFetcher<PaymentCouponResponse>({
            url: `/api/v2/payment/purchases/orders/${encodeURIComponent(requestDto.purchaseOrderId)}/coupons${orderCouponQuery(requestDto.paymentMethod)}`,
            method: 'GET',
            header: { Authorization: `Bearer ${requestDto.bearerToken}` },
          }).then((response) => response.data)
        }
        if (requestDto.productId) {
          return await podoFetcher<PaymentCouponResponse>({
            url: `/api/v1/products/${encodeURIComponent(requestDto.productId)}/coupons`,
            method: 'GET',
            header: {
              Authorization: `Bearer ${requestDto.bearerToken}`,
            },
          }).then((res) => res.data)
        }
        const searchParams = new URLSearchParams({ subscribeId: requestDto.subscribeId })
        return await podoFetcher<PaymentCouponResponse>({
          url: `/api/v1/coupon/getCouponsForPayment?${searchParams.toString()}`,
          method: 'GET',
          header: {
            Authorization: `Bearer ${requestDto.bearerToken}`,
          },
        }).then((res) => res.data)
      },
    }),

  getDiscountedPrice: (requestDto: RequestQueryOptions<CouponPaymentContext & { couponId: string }>) => ({
    queryKey: requestDto.purchaseOrderId
      ? [
          ...paymentEntityQueries.all,
          'preparedOrderDiscount',
          requestDto.purchaseOrderId,
          requestDto.couponId,
          requestDto.paymentMethod,
          requestDto.bearerToken,
        ]
      : requestDto.productId
        ? paymentEntityQueries.getProductDiscountedPriceQueryKey({
            productId: requestDto.productId,
            couponId: requestDto.couponId,
            bearerToken: requestDto.bearerToken,
          })
        : paymentEntityQueries.getDiscountedPriceQueryKey(requestDto),
    queryFn: async () => {
      if (requestDto.purchaseOrderId) {
        return await podoFetcher<BaseResponseSchema<number>>({
          url: `/api/v2/payment/purchases/orders/${encodeURIComponent(requestDto.purchaseOrderId)}/coupons/${encodeURIComponent(requestDto.couponId)}/discount${orderCouponQuery(requestDto.paymentMethod)}`,
          method: 'GET',
          header: { Authorization: `Bearer ${requestDto.bearerToken}` },
        })
      }
      if (requestDto.productId) {
        return await podoFetcher<BaseResponseSchema<number>>({
          method: 'GET',
          url: `/api/v1/products/${encodeURIComponent(requestDto.productId)}/coupons/${encodeURIComponent(requestDto.couponId)}/discount`,
          header: {
            Authorization: `Bearer ${requestDto.bearerToken}`,
          },
        })
      }
      const searchParams = new URLSearchParams({
        subscribeId: requestDto.subscribeId,
        couponId: requestDto.couponId,
      })
      return await podoFetcher<BaseResponseSchema<number>>({
        method: 'GET',
        url: `/api/v1/coupon/calculate?${searchParams.toString()}`,
        header: {
          Authorization: `Bearer ${requestDto.bearerToken}`,
        },
      })
    },
  }),

  getProductCouponForPayment: (requestDto: RequestQueryOptions<{ productId: string }>) =>
    paymentEntityQueries.getCouponForPayment({
      ...requestDto,
      subscribeId: '',
      productId: requestDto.productId,
    }),

  getProductDiscountedPrice: (requestDto: RequestQueryOptions<{ productId: string; couponId: string }>) =>
    paymentEntityQueries.getDiscountedPrice({
      ...requestDto,
      subscribeId: '',
      productId: requestDto.productId,
    }),
}
